import React, { useRef, useEffect } from "react"
import PropTypes from "prop-types"
import useProfileStore from "../profileStore"

export default function PersonalInfo({ labels, personalInfoRef, isGoogleLinked, lang, countries }) {
  const {
    headingPersonalInformation,
    labelName,
    labelBirthDay,
    labelBirthMonth,
    labelBirthYear,
    labelGender,
    labelStreetName,
    labelAreaCode,
    labelCity,
    labelCountryCode,
    selectListContextLabel,
    googleUnlinkButtonText,
    googleLinkButtonText,
    errorMessageFieldRequired,
  } = labels
  const {
    fullName,
    setFullName,
    birthDay,
    setBirthDay,
    birthMonth,
    setBirthMonth,
    birthYear,
    setBirthYear,
    gender,
    setGender,
    street,
    setStreet,
    postalCode,
    setPostalCode,
    city,
    setCity,
    setCountry,
    countryCode,
    setCountryCode,
    isFullNameValid,
    setIsFullNameValid,
    isCountryValid,
    setIsCountryValid,
    yearsArray,
    setYearsArray,
    monthsArray,
    setMonthsArray,
    daysArray,
    setDaysArray,
    gendersArray,
    setGendersArray,
    countriesArray,
    setCountriesArray,
  } = useProfileStore()
  const soMeLinkForm = useRef(undefined)
  const soMeUnLinkForm = useRef(undefined)

  useEffect(() => {
    // initialize dropdowns
    getDaysForDropdown()
    getMonthsForDropdown()
    getYearsForDropdown()
    getGendersForDropdown()
    getCountriesForDropdown()
  }, [])

  useEffect(() => {
    handleNameChange(fullName)
  }, [fullName])

  useEffect(() => {
    setIsCountryValid(!!countryCode)
  }, [countryCode])

  const handleSocialMediaClick = (elem) => {
    let loginProvider = elem.current.dataset.media
    let connected = elem.current.dataset.connected

    if (loginProvider) {
      if (connected) {
        let unlinkSocialMediaForm = soMeUnLinkForm.current
        unlinkSocialMediaForm.socialMedia.value = loginProvider
        unlinkSocialMediaForm.submit()
      } else {
        let linkSocialMediaForm = soMeLinkForm.current
        linkSocialMediaForm.socialMedia.value = loginProvider
        linkSocialMediaForm.submit()
      }
    } else {
      console.log("No loginProvider Value")
    }
  }

  // city auto complete
  const handlePostcodeInput = (postalCode) => {
    if (lang !== "da") return

    setPostalCode(postalCode)

    if (postalCode?.length === 4) {
      let apiurl = "https://dawa.aws.dk/postnumre/autocomplete?q={" + postalCode + "}"
      fetch(apiurl)
        .then((response) => {
          return response.json()
        })
        .then((data) => {
          setCity(data[0].postnummer.navn)
        })
        .catch((e) => {
          console.log(e.message)
        })
    }
  }

  const handleNameChange = (nameValue) => {
    if (nameValue.length > 0) {
      setIsFullNameValid(true)
    } else {
      setIsFullNameValid(false)
    }
  }

  //#region dropdown information
  const getDaysForDropdown = () => {
    const days = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16, 17, 18, 19, 20, 21, 22, 23, 24, 25, 26, 27, 28, 29, 30, 31]
    setDaysArray(days)
  }

  const getMonthsForDropdown = () => {
    const months = [
      { name: "January", danishName: "Januar", id: 1 },
      { name: "February", danishName: "Februar", id: 2 },
      { name: "March", danishName: "Marts", id: 3 },
      { name: "April", danishName: "April", id: 4 },
      { name: "May", danishName: "Maj", id: 5 },
      { name: "June", danishName: "Juni", id: 6 },
      { name: "July", danishName: "Juli", id: 7 },
      { name: "August", danishName: "August", id: 8 },
      { name: "September", danishName: "September", id: 9 },
      { name: "October", danishName: "Oktober", id: 10 },
      { name: "November", danishName: "November", id: 11 },
      { name: "December", danishName: "December", id: 12 },
    ]
    setMonthsArray(months)
  }

  const findMonthNameById = (id) => {
    const month = monthsArray.find((m) => m.id === id)
    return month ? (lang === "da" ? month.danishName : month.name) : null
  }

  const getMonthIdByName = (name) => {
    // Input is month id.
    if (typeof name === "number") return name + 1

    const monthObject = monthsArray.find((m) => (lang === "da" ? m.danishName === name : m.name === name))
    return monthObject ? monthObject.id : null
  }

  const getYearsForDropdown = () => {
    const currentYear = new Date().getFullYear()
    const years = []

    for (let year = currentYear; year >= 1900; year--) {
      years.push(year)
    }
    setYearsArray(years)
  }

  const getGendersForDropdown = () => {
    const genders = [
      { name: "Man", danishName: "Mand", id: 1 },
      { name: "Woman", danishName: "Kvinde", id: 2 },
    ]
    setGendersArray(genders)
  }

  const findGenderNameById = (Id) => {
    const gender = gendersArray.find((g) => parseInt(Id) === g.id)
    return gender ? (lang === "da" ? gender.danishName : gender.name) : null
  }
  const getGenderIdByName = (name) => {
    const genderObject = gendersArray.find((g) => (lang === "da" ? g.danishName === name : g.name === name))
    return genderObject ? genderObject.id : null
  }

  const getCountriesForDropdown = () => {
    setCountriesArray(countries || [{}])
  }

  const findCountryByIsoCode = (isoCode) => {
    const countryObject = countries.find((i) => i.Value === isoCode)
    return countryObject ? countryObject.Text : null
  }

  const findIsoCodeByCountry = (c) => {
    const countryObject = countries.find((i) => i.Text === c)
    return countryObject ? countryObject.Value : null
  }

  const handleCountryChange = (c) => {
    const isoCode = findIsoCodeByCountry(c)
    setCountryCode(isoCode)
    setCountry(findCountryByIsoCode(isoCode))
  }

  return (
    <div className="form--profile__essentials" ref={personalInfoRef}>
      <div className="form__section form__section--no-border clearfix">
        <h3 className="profile-section-header">{headingPersonalInformation}</h3>
        <div className="form__section__inner">
          <div className={`form__field form__field--text is-required ${fullName ? "is-filled" : ""} ${isFullNameValid ? "" : "is-invalid"} `}>
            <span className="form__valid"></span>

            {/* Name Field */}
            <input
              id="Name"
              onChange={(e) => {
                setFullName(e.target.value)
              }}
              label={labelName}
              className="form__item "
              name="Name"
              tabIndex=""
              type="text"
              value={fullName || ""}
              placeholder={""}
              autoComplete="namename"
            ></input>
            <label htmlFor="Name" className="form__label">
              <span className="form__label__text">
                {labelName}&nbsp;&nbsp;<strong>&nbsp;</strong>
              </span>
            </label>
            {isFullNameValid ? undefined : (
              <span id="fullnameFormError" className="form-notice">
                {errorMessageFieldRequired}
              </span>
            )}
          </div>

          {/* Birth Date Field Group */}
          <div className="birthday-picker">
            {/* Day */}
            <div className={`form__field form__field--select ${birthDay ? "is-filled" : ""}`}>
              <div className="form__container">
                <span className="form__valid"></span>
                <select
                  id="selectday"
                  value={birthDay.toString() || ""}
                  onChange={(e) => setBirthDay(e.target.value)}
                  name="Day"
                  className="form__item "
                  aria-label={`${selectListContextLabel} ${labelBirthDay}`}
                  autoComplete="bday-day"
                >
                  <option value=""></option>
                  {daysArray.map((item, index) => (
                    <option key={index}>{item}</option>
                  ))}
                </select>
                <label htmlFor="selectday" className="form__label">
                  <span className="form__label__text">
                    {labelBirthDay}&nbsp;&nbsp;<strong>&nbsp;</strong>
                  </span>
                </label>
              </div>
            </div>

            {/* Month */}
            <div className={`form__field form__field--select ${birthMonth ? "is-filled" : ""}`}>
              <div className="form__container">
                <span className="form__valid"></span>
                <select
                  id="selectmonth"
                  value={findMonthNameById(birthMonth) || ""}
                  onChange={(e) => setBirthMonth(getMonthIdByName(e.target.value))}
                  name="Month"
                  className="form__item "
                  aria-label={`${selectListContextLabel} ${labelBirthMonth}`}
                  autoComplete="bday-month"
                >
                  <option value="default"></option>
                  {monthsArray.map((item, index) => (
                    <option key={index}>{lang === "da" ? item.danishName : item.name}</option>
                  ))}
                </select>
                <label htmlFor="selectmonth" className="form__label">
                  <span className="form__label__text">
                    {labelBirthMonth}&nbsp;&nbsp;<strong>&nbsp;</strong>
                  </span>
                </label>
              </div>
            </div>

            {/* Year */}
            <div className={`form__field form__field--select ${birthYear ? "is-filled" : ""}`}>
              <div className="form__container">
                <span className="form__valid"></span>
                <select
                  id={"selectyear"}
                  value={birthYear.toString() || ""}
                  onChange={(e) => setBirthYear(e.target.value)}
                  name="Year"
                  className="form__item"
                  aria-label={`${selectListContextLabel} ${labelBirthYear}`}
                  autoComplete="bday-year"
                >
                  <option value=""></option>
                  {yearsArray.map((item, index) => (
                    <option key={index}>{item}</option>
                  ))}
                </select>
                <label htmlFor="selectyear" className="form__label">
                  <span className="form__label__text">
                    {labelBirthYear}&nbsp;&nbsp;<strong>&nbsp;</strong>
                  </span>
                </label>
              </div>
            </div>
          </div>

          {/* Gender */}
          <div className={`form__field form__field--select is-filled`}>
            <div className="form__container">
              <span className="form__valid"></span>
              <select
                value={findGenderNameById(gender) || ""}
                id="selectgender"
                onChange={(e) => setGender(getGenderIdByName(e.target.value))}
                name="Gender"
                className="form__item"
                aria-label={`${selectListContextLabel} ${labelGender}`}
                autoComplete="sex"
              >
                {gendersArray.map((item, index) => (
                  <option key={index}>{lang === "da" ? item.danishName : item.name}</option>
                ))}
              </select>
              <label htmlFor="selectgender" className="form__label">
                <span className="form__label__text">
                  {labelGender}&nbsp;&nbsp;<strong>&nbsp;</strong>
                </span>
              </label>
            </div>
          </div>
        </div>
      </div>

      {/* Street Name */}
      <div className="form__section form__section--no-border clearfix">
        <div className="form__section__inner">
          {/* Street */}
          <div className={`form__field form__field--text ${street ? "is-filled" : ""}`}>
            <span className="form__valid"></span>
            <input
              id="StreetName"
              label={labelStreetName}
              className="form__item "
              name="StreetName"
              tabIndex=""
              type="text"
              value={street}
              onChange={(e) => setStreet(e.target.value)}
              placeholder=""
              autoComplete="address-line1"
            ></input>
            <label htmlFor="StreetName" className="form__label">
              <span className="form__label__text">
                {labelStreetName}&nbsp;&nbsp;<strong>&nbsp;</strong>
              </span>
            </label>
          </div>

          {/* Zip Code */}
          <div className={`form__field form__field--text ${postalCode ? "is-filled" : ""}`}>
            <span className="form__valid"></span>
            <input
              id="ZipCode"
              className="form__item "
              name="ZipCode"
              tabIndex=""
              type="text"
              value={postalCode}
              onChange={(e) => handlePostcodeInput(e.target.value)}
              placeholder=""
              autoComplete="postal-code"
            ></input>

            <label htmlFor="ZipCode" className="form__label">
              <span className="form__label__text">
                {labelAreaCode}&nbsp;&nbsp;<strong>&nbsp;</strong>
              </span>
            </label>
          </div>

          {/* City */}
          <div className={`form__field form__field--text ${city ? "is-filled" : ""}`}>
            <span className="form__valid"></span>
            <input
              id="City"
              label={labelCity}
              className="form__item "
              name="City"
              tabIndex=""
              type="text"
              value={city}
              onChange={(e) => setCity(e.target.value)}
              placeholder=""
              autoComplete="address-level2"
            ></input>
            <label htmlFor="City" className="form__label">
              <span className="form__label__text">
                {labelCity}&nbsp;&nbsp;<strong>&nbsp;</strong>
              </span>
            </label>
          </div>

          {/* Country */}
          <div className={`form__field form__field--select is-required is-filled js--countries-choices ${isCountryValid ? "" : "is-invalid"}`}>
            <div className="form__container">
              <span className="form__valid"></span>
              <select
                value={findCountryByIsoCode(countryCode) || ""}
                onChange={(e) => handleCountryChange(e.target.value)}
                id="selectcountry"
                label={labels.labelCountryCode}
                className="form__item js--select form__field--country"
                name="CountryCode"
                aria-label={`${selectListContextLabel} ${labelCountryCode}`}
                tabIndex=""
                autoComplete="country"
              >
                <option value=""></option>
                {countriesArray.map((item, index) => (
                  <option key={index}>{item.Text}</option>
                ))}
              </select>
              <label htmlFor="selectcountry" className="form__label">
                <span className="form__label__text">
                  {labelCountryCode}&nbsp;&nbsp;<strong>&nbsp;</strong>
                </span>
              </label>
              {isCountryValid ? undefined : (
                <span id="countryFormError" className="form-notice">
                  {errorMessageFieldRequired}
                </span>
              )}
            </div>
          </div>
        </div>
      </div>
      <div className="social-connect">
        {/* <a className="button button--medium button--facebook has-icon" data-media="Facebook" data-connected={facebookLink} type="submit" onClick="handleSocialMediaClick(facebookForm) ref={facebookForm}">
                            <span className="icon">
                                <svg className="svg-icon svg-icon--socialfacebook">
                                    <use href="/dist/svg/symbols.svg#svg-socialfacebook">&nbsp;</use>
                                </svg>
                            </span>
                            <span className="text">{facebookLink ? facebookUnlinkButtonText : facebookLinkButtonText}</span>
                        </a> */}
        <a
          className="button button--medium button--googleplus has-icon"
          data-media="Google"
          data-connected={isGoogleLinked}
          title={lang == "da" ? "Forbind med Google" : "Link your Google account"}
          onClick={(e) => handleSocialMediaClick(e)}
          type="submit"
          tabIndex={0}
        >
          <span className="icon">
            <svg tabIndex={"-1"} aria-hidden="true" className="svg-icon svg-icon--socialgoogleplus">
              <use href="/dist/svg/symbols.svg#svg-socialgoogleplus">&nbsp;</use>
            </svg>
          </span>
          <span className="text">{isGoogleLinked ? googleUnlinkButtonText : googleLinkButtonText}</span>
        </a>
      </div>
      <form action="/IdpImplicitFlow/LinkAccount" id="socialLinkAccountForm" method="post" hidden ref={soMeLinkForm}>
        <input id="socialMedia" name="socialMedia" type="hidden" value="" />
        <input type="submit" hidden={true} />
      </form>

      <form action="/IdpImplicitFlow/UnlinkAccount" id="socialUnlinkAccountForm" method="post" hidden ref={soMeUnLinkForm}>
        <input id="socialMedia" name="socialMedia" type="hidden" value="" />
        <input type="submit" hidden={true} />
      </form>
    </div>
  )
}

PersonalInfo.propTypes = {
  labels: PropTypes.object.isRequired,
  isGoogleLinked: PropTypes.bool.isRequired,
  lang: PropTypes.string.isRequired,
  countries: PropTypes.array.isRequired,
  personalInfoRef: PropTypes.object.isRequired,
}
